import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useToast } from "@chakra-ui/react";
import { FiImage, FiRefreshCw, FiTrash2, FiUpload, FiX } from "react-icons/fi";

const GENDERS = ["male", "female", "both"];

const CarouselBannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [gender, setGender] = useState("male");
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const loadBanners = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get("/api/v1/carouselBanner/admin");
      setBanners(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Failed to load carousel banners:", error);
      setBanners([]);
      toast({
        title: "Unable to load carousel banners",
        description: error.response?.data?.message || "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBanners();
    // This manager fetches only when its Store Management tab is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeSelectedFile = (index) => {
    setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    const images = selected.filter((file) => file.type.startsWith("image/"));

    if (selected.length !== images.length) {
      toast({ title: "Some files were skipped", description: "Only image files can be used as carousel banners.", status: "warning", duration: 4000, isClosable: true });
    }

    setFiles((current) => [...current, ...images].slice(0, 10));
    if (selected.length + files.length > 10) {
      toast({ title: "Maximum 10 images", description: "Only the first 10 selected images are kept.", status: "warning", duration: 4000, isClosable: true });
    }
    event.target.value = "";
  };

  const uploadBanners = async () => {
    if (!files.length) {
      toast({ title: "Select at least one image", status: "warning", duration: 3000, isClosable: true });
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));
    formData.append("gender", gender);

    try {
      setIsUploading(true);
      await axios.post("/api/v1/carouselBanner", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles([]);
      toast({ title: "Carousel banners uploaded", status: "success", duration: 3000, isClosable: true });
      await loadBanners();
    } catch (error) {
      console.error("Failed to upload carousel banners:", error);
      toast({ title: "Banner upload failed", description: error.response?.data?.message || "Please try again.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsUploading(false);
    }
  };

  const toggleBannerGroup = async (banner) => {
    try {
      setActionId(banner._id);
      await axios.patch(`/api/v1/carouselBanner/admin/toggle/${banner._id}`);
      await loadBanners();
      toast({ title: "Banner group updated", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      console.error("Failed to toggle carousel banner group:", error);
      toast({ title: "Unable to update banner group", description: error.response?.data?.message || "Please try again.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setActionId(null);
    }
  };

  const deleteBannerImage = async (banner, image) => {
    if (!window.confirm("Delete this carousel banner image?")) return;
    const imageKey = `${banner._id}:${image.publicId}`;

    try {
      setActionId(imageKey);
      await axios.delete(`/api/v1/carouselBanner/${banner._id}`, { data: { publicId: image.publicId } });
      await loadBanners();
      toast({ title: "Carousel banner deleted", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      console.error("Failed to delete carousel banner:", error);
      toast({ title: "Unable to delete carousel banner", description: error.response?.data?.message || "Please try again.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setActionId(null);
    }
  };

  return (
    <section className="space-y-6" aria-labelledby="carousel-banners-heading">
      <div className="rounded-2xl border-2 border-[var(--yellow)]/20 bg-gradient-to-br from-gray-900 via-gray-800 to-black p-5 shadow-lg">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 id="carousel-banners-heading" className="text-2xl font-bold text-[var(--yellow)]">Carousel Banners</h2>
            <p className="mt-1 text-sm text-[color:var(--yellow)/0.8]">Upload and manage the banners shown in the mobile app carousel.</p>
          </div>
          <button type="button" onClick={loadBanners} disabled={isLoading || isUploading} className="flex items-center justify-center gap-2 rounded-md border border-[var(--yellow)]/40 px-4 py-2 text-sm font-medium text-[var(--yellow)] transition hover:bg-[var(--yellow)]/10 disabled:cursor-not-allowed disabled:opacity-50">
            <FiRefreshCw className={isLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-[auto_1fr_auto] lg:items-end">
          <label className="block text-sm font-medium text-white">
            Audience
            <select value={gender} onChange={(event) => setGender(event.target.value)} disabled={isUploading} className="mt-2 block w-full rounded-md border border-gray-600 bg-gray-950 px-3 py-2 text-white outline-none focus:border-[var(--yellow)] disabled:opacity-50">
              {GENDERS.map((value) => <option key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</option>)}
            </select>
          </label>
          <div>
            <p className="text-sm font-medium text-white">New images</p>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="mt-2 flex items-center gap-2 rounded-md border border-dashed border-[var(--yellow)]/50 px-4 py-2 text-sm font-medium text-[var(--yellow)] transition hover:bg-[var(--yellow)]/10 disabled:opacity-50">
              <FiUpload /> Select images
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
          </div>
          <button type="button" onClick={uploadBanners} disabled={isUploading || !files.length} className="flex items-center justify-center gap-2 rounded-md bg-[var(--yellow)] px-4 py-2 text-sm font-medium text-[var(--black)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">
            <FiUpload /> {isUploading ? "Uploading..." : "Upload banners"}
          </button>
        </div>

        {files.length > 0 && <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          {files.map((file, index) => <div key={`${file.name}-${index}`} className="relative overflow-hidden rounded-lg border border-gray-700 bg-gray-950 p-2">
            <p className="truncate pr-6 text-xs text-gray-300">{file.name}</p>
            <button type="button" onClick={() => removeSelectedFile(index)} className="absolute right-1 top-1 rounded p-1 text-red-300 hover:bg-red-500/20" aria-label={`Remove ${file.name}`}><FiX /></button>
          </div>)}
        </div>}
      </div>

      {isLoading ? <div className="flex min-h-64 items-center justify-center rounded-2xl border-2 border-gray-700 bg-gray-900"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[var(--yellow)]/25 border-t-[var(--yellow)]" /></div>
        : banners.length === 0 ? <div className="rounded-2xl border-2 border-dashed border-gray-700 bg-gray-900 p-12 text-center"><FiImage className="mx-auto mb-4 text-4xl text-[var(--yellow)]" /><h3 className="text-lg font-semibold text-white">No carousel banners yet</h3><p className="mt-1 text-sm text-gray-400">Choose an audience, then upload one or more images.</p></div>
        : banners.map((banner) => <article key={banner._id} className="rounded-2xl border-2 border-[var(--yellow)]/20 bg-gray-900 p-5 shadow-lg">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div><h3 className="text-lg font-semibold capitalize text-white">{banner.gender} banners</h3><p className="text-sm text-gray-400">{banner.images?.length || 0} image(s)</p></div>
            <button type="button" onClick={() => toggleBannerGroup(banner)} disabled={actionId === banner._id} className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${banner.isActive ? "bg-green-600 hover:bg-green-500" : "bg-gray-600 hover:bg-gray-500"}`}>
              {actionId === banner._id ? "Updating..." : banner.isActive ? "Active" : "Disabled"}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {(banner.images || []).map((image, index) => { const imageKey = `${banner._id}:${image.publicId}`; return <div key={image.publicId || index} className="group relative overflow-hidden rounded-xl border border-gray-700 bg-black"><img src={image.url} alt={`${banner.gender} carousel banner ${index + 1}`} className="aspect-[16/9] w-full object-cover" /><button type="button" onClick={() => deleteBannerImage(banner, image)} disabled={actionId === imageKey} className="absolute right-2 top-2 rounded-md bg-red-600 p-2 text-white opacity-0 transition hover:bg-red-500 group-hover:opacity-100 group-focus-within:opacity-100 disabled:opacity-50" aria-label={`Delete ${banner.gender} carousel banner ${index + 1}`}><FiTrash2 /></button></div>; })}
          </div>
        </article>)}
    </section>
  );
};

export default CarouselBannerManager;
